import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FaCloudArrowUp } from "react-icons/fa6";
import { Check } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { toast } from "../../utils/toast";
import {
  fetchAllInventoryUploadRejectedRows,
  fetchInventoryUploadRows,
  fetchInventoryUploadStatus,
  uploadInventoryExcel,
} from "../../service/MeterInventoryManagement";
import {
  downloadRejectedRowsExcel,
  rejectedRowsFileNamePrefix,
} from "../../utils/inventoryRejectedRowsExport";
import {
  apiErrorMessage,
  apiSuccessMessage,
  firstArray,
  firstNumber,
  getInventoryPermissions,
  unwrap,
} from "./inventoryFormShared";
import { readSheetHeaders } from "./inventoryUploadColumns";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
// Only the first handful of rejected rows are shown inline; the rest come from the
// paged rows endpoint on demand, so a sheet with thousands of rejects cannot turn
// the screen into a log viewer.
export const MAX_VISIBLE_ROW_ERRORS = 8;
const REJECT_PAGE_SIZE = 50;
const POLL_INTERVAL_MS = 2500;
const TERMINAL_STATUSES = new Set([
  "COMPLETED",
  "COMPLETED_WITH_ERRORS",
  "FAILED",
  "CANCELLED",
]);

export const formatFileSize = (size) => {
  if (size < 1024) return `${size} bytes`;
  if (size < 1048576) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1048576).toFixed(1)} MB`;
};

const hasAcceptedExtension = (fileName = "") =>
  ACCEPTED_EXTENSIONS.some((ext) => fileName.toLowerCase().endsWith(ext));

// A sheet upload offers no create-only mode, so create access alone unlocks
// "create new or update existing" here -- unlike the manual add form, where the
// combined mode competes with a dedicated create-only option.
export const useUploadModePermissions = () => {
  const inventoryPerm = useMemo(() => getInventoryPermissions(), []);
  return useMemo(
    () => ({
      CREATE_OR_UPDATE: Boolean(inventoryPerm.canCreate),
      UPDATE_ONLY: Boolean(inventoryPerm.canUpdate),
    }),
    [inventoryPerm]
  );
};

export const defaultUploadModeFor = (modePermissions) =>
  modePermissions.CREATE_OR_UPDATE
    ? "CREATE_OR_UPDATE"
    : modePermissions.UPDATE_ONLY
      ? "UPDATE_ONLY"
      : "";

export const UploadStatusBadge = ({ status, t }) => {
  const code = String(status || "").toUpperCase();
  const label =
    {
      QUEUED: t.inventoryImportStatusQueued,
      PENDING: t.inventoryImportStatusQueued,
      IN_PROGRESS: t.inventoryImportStatusInProgress,
      PROCESSING: t.inventoryImportStatusInProgress,
      COMPLETED: t.inventoryImportStatusCompleted,
      COMPLETED_WITH_ERRORS: t.inventoryImportStatusCompleted,
      SUCCESS: t.inventoryImportStatusCompleted,
      FAILED: t.inventoryImportStatusFailedLabel,
      CANCELLED: t.inventoryImportStatusFailedLabel,
    }[code] || status || t.notAvailable;
  const tone =
    code === "COMPLETED" || code === "SUCCESS"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : code === "FAILED" || code === "CANCELLED"
        ? "border-red-200 bg-red-50 text-red-700"
        : "border-amber-200 bg-amber-50 text-amber-700";
  return (
    // whitespace-nowrap is load-bearing: the status column is narrow, and a wrapping
    // two-word label (e.g. "In progress") overflows the fixed-height pill.
    <span
      className={`inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-md border px-2 text-[11px] font-semibold leading-none ${tone}`}
    >
      {label}
    </span>
  );
};

export const SummaryTile = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    red: "border-red-200 bg-red-50 text-red-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${tones[tone] || tones.slate}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
};

export const RejectedRowList = ({ rows, t }) => (
  <ul className="space-y-2">
    {rows.map((row, index) => {
      const rowNumber = row?.rowNumber ?? row?.row ?? row?.rowIndex ?? null;
      const identifier = row?.identifier ?? "";
      const reason = row?.reason ?? row?.message ?? row?.error ?? "";
      return (
        <li
          key={`${rowNumber ?? "row"}-${identifier}-${index}`}
          className="rounded-lg border border-red-100 bg-red-50/60 px-3 py-2 text-xs text-red-700"
        >
          <span className="font-semibold">
            {rowNumber !== null ? `${t.inventoryImportRow} ${rowNumber}` : t.inventoryImportRow}
            {identifier ? ` · ${identifier}` : ""}
            {row?.rowStatus ? ` · ${row.rowStatus}` : ""}:{" "}
          </span>
          <span>{reason}</span>
        </li>
      );
    })}
  </ul>
);

// Click-or-drop file picker. The hidden input is owned by the caller's ref so the
// same control can be reset and reopened from a toolbar button.
export const UploadDropZone = ({ upload, t, accept = ".csv,.xlsx,.xls", disabled = false, showColumnCount = true }) => (
  <>
    <input
      ref={upload.fileInputRef}
      id="inventory-import-file"
      type="file"
      className="hidden"
      accept={accept}
      disabled={disabled}
      onChange={(event) => upload.selectFile(event.target.files?.[0])}
      data-testid="inventory-import-file-input"
    />
    <button
      type="button"
      disabled={disabled}
      onClick={upload.openFilePicker}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) upload.setIsDraggingFile(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={() => upload.setIsDraggingFile(false)}
      onDrop={(event) => { event.preventDefault(); if (!disabled) upload.handleFileDrop(event); }}
      data-testid="inventory-import-dropzone"
      className={`flex h-[200px] w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed transition ${
        upload.pendingFile
          ? "border-emerald-400 bg-emerald-50/60"
          : upload.isDraggingFile
            ? "border-[#3F63EA] bg-blue-50"
            : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"
      }`}
    >
      <span
        className={`flex h-12 w-12 items-center justify-center rounded-full ${
          upload.pendingFile ? "bg-emerald-100 text-emerald-600" : "bg-blue-50 text-[#2563EB]"
        }`}
      >
        {upload.pendingFile ? <Check size={22} strokeWidth={2.2} /> : <FaCloudArrowUp size={23} />}
      </span>
      {upload.readingHeaders ? (
        <span className="mt-4 text-sm font-semibold text-slate-500">{t.Processing}</span>
      ) : upload.pendingFile ? (
        <>
          <span className="mt-4 max-w-full truncate px-6 text-sm font-semibold text-slate-700">
            {upload.pendingFile.name}
          </span>
          <span className="mt-1 text-xs text-slate-400">
            {formatFileSize(upload.pendingFile.size)} · {showColumnCount && `${upload.colHeaders.length} ${t.inventoryImportColumnsFound} · `}{t.inventoryImportChooseAnother}
          </span>
        </>
      ) : (
        <>
          <span className="mt-4 text-sm font-semibold text-slate-700">
            {t.inventoryImportDropTitle}
          </span>
          <span className="mt-1 text-xs text-slate-400">{t.inventoryImportDropHint}</span>
        </>
      )}
    </button>
  </>
);

/**
 * One excel-upload "run": the POST only acknowledges the sheet, so the ack is kept
 * alongside the latest polled status until the run reaches a terminal state. Owns
 * the picked file, the run state, the rejected-row paging and the Excel export;
 * the destination fields stay with the screen and are handed to `startRun`.
 *
 * @param {Object} scope - `{ projectId, organizationId }` from `useInventoryScope`.
 * @param {Function} scope.onRunFinished - Called whenever a run reaches a terminal status.
 */
export const useInventoryUploadRun = ({ projectId, organizationId, onRunFinished } = {}) => {
  const { langMode } = useContext(AppContext);
  const t = translations[langMode] || translations.en;
  const fileInputRef = useRef(null);

  const [pendingFile, setPendingFile] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  // Header row of the picked sheet -- the options offered by the column mapping.
  const [colHeaders, setColHeaders] = useState([]);
  const [readingHeaders, setReadingHeaders] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [runAck, setRunAck] = useState(null);
  const [runId, setRunId] = useState(null);
  const [runValidateOnly, setRunValidateOnly] = useState(false);
  const [runStatus, setRunStatus] = useState(null);
  const [runError, setRunError] = useState("");
  const [polling, setPolling] = useState(false);
  const [importDone, setImportDone] = useState(false);

  const [extraRejects, setExtraRejects] = useState(null);
  const [rejectPage, setRejectPage] = useState(0);
  const [rejectsLoading, setRejectsLoading] = useState(false);
  const [rejectsExhausted, setRejectsExhausted] = useState(false);
  const [rejectsExporting, setRejectsExporting] = useState(false);

  const resetRun = useCallback(() => {
    setSubmitting(false);
    setUploadProgress(0);
    setRunAck(null);
    setRunId(null);
    setRunStatus(null);
    setRunError("");
    setPolling(false);
    setImportDone(false);
    setExtraRejects(null);
    setRejectPage(0);
    setRejectsExhausted(false);
    setRejectsExporting(false);
  }, []);

  const resetAll = useCallback(() => {
    setPendingFile(null);
    setColHeaders([]);
    setIsDraggingFile(false);
    resetRun();
  }, [resetRun]);

  // Poll the run until it reaches a terminal status. The timeout chain (rather than
  // setInterval) keeps a slow response from stacking up overlapping requests.
  useEffect(() => {
    if (!runId || !polling) return undefined;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const detail = unwrap(
          await fetchInventoryUploadStatus(runId, { organizationId, projectId })
        );
        if (cancelled) return;
        setRunStatus(detail);
        const status = String(detail?.status || "").toUpperCase();
        if (TERMINAL_STATUSES.has(status)) {
          setPolling(false);
          // Whatever the backend reported about the run is what the operator sees --
          // its own message/failureReason wins over the generic translated string.
          if (status === "FAILED" || status === "CANCELLED") {
            toast.error(detail?.failureReason || apiSuccessMessage(detail, t.inventoryImportFailed));
          } else if (!runValidateOnly) {
            setImportDone(true);
            toast.success(apiSuccessMessage(detail, t.inventoryImportSuccess));
          } else {
            const validationMessage = apiSuccessMessage(detail, "");
            if (validationMessage) toast.info(validationMessage);
          }
          onRunFinished?.();
          return;
        }
      } catch (error) {
        if (cancelled) return;
        setRunError(apiErrorMessage(error, t.inventoryImportPollFailed));
        setPolling(false);
        return;
      }
      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, polling, runValidateOnly]);

  // The headers are read up front so an unreadable or header-less sheet is rejected
  // here, before the operator reaches the column mapping.
  const selectFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!hasAcceptedExtension(file.name)) {
        toast.warn(t.inventoryImportInvalidType);
        return;
      }
      setReadingHeaders(true);
      try {
        const headers = await readSheetHeaders(file);
        if (headers.length === 0) {
          toast.warn(t.inventoryImportNoHeaders);
          return;
        }
        setPendingFile(file);
        setColHeaders(headers);
        // A different sheet invalidates whatever the previous run reported.
        resetRun();
      } catch (error) {
        toast.error(t.inventoryImportHeadersReadFailed);
      } finally {
        setReadingHeaders(false);
      }
    },
    [resetRun, t]
  );

  const openFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  }, []);

  const handleFileDrop = useCallback(
    (event) => {
      event.preventDefault();
      setIsDraggingFile(false);
      selectFile(event.dataTransfer.files?.[0]);
    },
    [selectFile]
  );

  /**
   * Send the picked sheet. `validateOnly` defaults to false: the import writes in a
   * single pass, with no separate dry run. `columnMappings` is sent as given.
   * Resolves to the backend's acknowledgement, or null when the upload was rejected.
   */
  const startRun = useCallback(
    async ({
      storeId,
      itemType,
      uploadMode,
      updateAction,
      columnMappings,
      validateOnly = false,
    }) => {
      if (!pendingFile || !storeId) return;
      resetRun();
      setRunValidateOnly(validateOnly);
      setSubmitting(true);
      try {
        const ack = unwrap(
          await uploadInventoryExcel(
            {
              file: pendingFile,
              storeId,
              projectId,
              itemType,
              uploadMode,
              updateAction,
              organizationId,
              validateOnly,
              columnMappings,
            },
            {
              onUploadProgress: (event) => {
                if (!event.total) return;
                setUploadProgress(Math.round((event.loaded * 100) / event.total));
              },
            }
          )
        );
        setRunAck(ack);
        // The acknowledgement carries the backend's own wording ("N row(s) accepted
        // and being imported in the background"), so surface it verbatim.
        const ackMessage = apiSuccessMessage(ack, "");
        const id = ack?.inventoryUploadId ?? ack?.uploadId ?? ack?.id ?? null;
        if (id) {
          if (ackMessage) toast.info(ackMessage);
          setRunId(id);
          setPolling(true);
          // The run is already listed as IN_PROGRESS, so refresh the history behind.
          onRunFinished?.();
        } else {
          // Defensive: an older/synchronous backend would answer with the result itself.
          setRunStatus(ack);
          if (!validateOnly) {
            setImportDone(true);
            toast.success(ackMessage || t.inventoryImportSuccess);
          } else if (ackMessage) {
            toast.info(ackMessage);
          }
          onRunFinished?.();
        }
        return ack;
      } catch (error) {
        // Unreadable file, missing serial column, empty sheet, bad mappings and invalid
        // store/project still fail synchronously.
        setRunError(
          apiErrorMessage(
            error,
            validateOnly ? t.inventoryImportValidationError : t.inventoryImportFailed
          )
        );
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [pendingFile, projectId, organizationId, resetRun, onRunFinished, t]
  );

  const summary = useMemo(() => {
    const source = runStatus || runAck;
    if (!source) return null;
    const notUploaded = firstArray(
      source.notUploaded,
      source.errors,
      source.rowErrors,
      source.failures
    );
    return {
      status: String(source.status || "").toUpperCase(),
      validateOnly: Boolean(source.validateOnly ?? runValidateOnly),
      message: source.message || source.statusMessage || "",
      failureReason: source.failureReason || "",
      total: firstNumber(source.totalRowCount, source.totalRows, source.total),
      created: firstNumber(source.createdCount, source.created),
      updated: firstNumber(source.updatedCount, source.updated),
      skipped: firstNumber(source.skippedCount, source.skipped),
      failed: firstNumber(source.failedCount, source.failureCount, source.errorCount),
      notUploadedCount: firstNumber(source.notUploadedCount, notUploaded.length),
      notUploaded,
      notUploadedTruncated: Boolean(source.notUploadedTruncated),
      failureLogUrl: source.failureLogUrl || "",
    };
  }, [runStatus, runAck, runValidateOnly]);

  const isRunning = submitting || polling;
  const isTerminal = Boolean(summary && TERMINAL_STATUSES.has(summary.status));
  const runFailed = summary?.status === "FAILED" || summary?.status === "CANCELLED";
  const hasIssues = Boolean(
    summary && ((summary.notUploadedCount ?? 0) > 0 || (summary.failed ?? 0) > 0)
  );
  const visibleRejects =
    extraRejects ?? (summary?.notUploaded || []).slice(0, MAX_VISIBLE_ROW_ERRORS);

  const loadMoreRejects = async () => {
    if (!runId) return;
    setRejectsLoading(true);
    try {
      const payload = unwrap(
        await fetchInventoryUploadRows(runId, {
          page: rejectPage,
          size: REJECT_PAGE_SIZE,
          rowStatus: "FAILED",
          organizationId,
          projectId,
        })
      );
      const items = Array.isArray(payload)
        ? payload
        : firstArray(payload?.rows, payload?.content, payload?.items, payload?.data);
      setExtraRejects((prev) => [...(prev || []), ...items]);
      setRejectPage((prev) => prev + 1);
      if (items.length < REJECT_PAGE_SIZE) setRejectsExhausted(true);
    } catch (error) {
      toast.error(apiErrorMessage(error, t.inventoryImportPollFailed));
    } finally {
      setRejectsLoading(false);
    }
  };

  // The status payload truncates its `notUploaded` array, so the export walks the
  // paged rows endpoint for the complete list and falls back to whatever is already
  // on screen when that is unavailable.
  const downloadRejects = async () => {
    const onScreen = extraRejects ?? summary?.notUploaded ?? [];
    const namePrefix = rejectedRowsFileNamePrefix({
      fileName: runStatus?.fileName || pendingFile?.name || "",
      id: runId,
    });
    setRejectsExporting(true);
    try {
      let rejected = onScreen;
      if (runId) {
        const fetched = await fetchAllInventoryUploadRejectedRows(runId, {
          organizationId,
          projectId,
        });
        if (fetched.length > 0) rejected = fetched;
      }
      if (!downloadRejectedRowsExcel(rejected, namePrefix)) {
        toast.info(t.inventoryImportNotUploadedExportEmpty);
      }
    } catch (error) {
      if (downloadRejectedRowsExcel(onScreen, namePrefix)) {
        toast.warn(t.inventoryImportNotUploadedExportPartial);
      } else {
        toast.error(apiErrorMessage(error, t.inventoryImportNotUploadedExportFailed));
      }
    } finally {
      setRejectsExporting(false);
    }
  };

  return {
    fileInputRef,
    pendingFile,
    colHeaders,
    readingHeaders,
    isDraggingFile,
    setIsDraggingFile,
    selectFile,
    openFilePicker,
    handleFileDrop,

    submitting,
    uploadProgress,
    runError,
    runId,
    runValidateOnly,
    summary,
    importDone,
    isRunning,
    isTerminal,
    runFailed,
    hasIssues,
    startRun,
    resetRun,
    resetAll,

    visibleRejects,
    extraRejects,
    rejectsLoading,
    rejectsExhausted,
    rejectsExporting,
    loadMoreRejects,
    downloadRejects,
  };
};
